import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Search as SearchIcon,
  WorkOutline as RoleIcon
} from '@mui/icons-material';
import StaffingEmployeeSelect from './StaffingEmployeeSelect';

const TEAL = '#0d9488';
const TEAL_DARK = '#0f766e';

function parseSalaryLineItems(raw) {
  let items = raw || [];
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      items = [];
    }
  }
  return Array.isArray(items) ? items : [];
}

function lineItemsToSingleFields(allowances, deductions) {
  const allowanceSum = parseSalaryLineItems(allowances).reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const deductionSum = parseSalaryLineItems(deductions).reduce((s, d) => s + (Number(d.amount) || 0), 0);
  return { allowance: allowanceSum, deduction: deductionSum };
}

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function monthlyGross(basic, hra, allowance) {
  return (Number(basic) || 0) + (Number(hra) || 0) + (Number(allowance) || 0);
}

const emptyRoleForm = { id: null, name: '', basic: '', hra: '', allowance: '', deduction: '' };
const emptyAssignForm = {
  candidate_id: '',
  salary_role_id: '',
  basic: '',
  hra: '',
  allowance: '',
  deduction: ''
};

export default function StaffingSalaryStructureTab({ apiRequest, activeEmployeesList, onFeedback }) {
  const [section, setSection] = useState(0);
  const [salaryRoles, setSalaryRoles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleSearch, setRoleSearch] = useState('');
  const [assignSearch, setAssignSearch] = useState('');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [assignForm, setAssignForm] = useState(emptyAssignForm);
  const [saving, setSaving] = useState(false);
  const [assignLockEmployee, setAssignLockEmployee] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, assignRes] = await Promise.all([
        apiRequest('/api/company/hrms/payroll/salary-roles'),
        apiRequest('/api/company/hrms/payroll/salary-structures')
      ]);
      setSalaryRoles(rolesRes.roles || []);
      setAssignments(assignRes.assignments || []);
    } catch (e) {
      onFeedback(e.message);
      setSalaryRoles([]);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, onFeedback]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase();
    if (!q) return salaryRoles;
    return salaryRoles.filter((r) => r.name?.toLowerCase().includes(q));
  }, [salaryRoles, roleSearch]);

  const filteredAssignments = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    if (!q) return assignments;
    return assignments.filter((row) => {
      const hay = [row.employee_name, row.employee_code, row.mobile, row.salary_role_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [assignments, assignSearch]);

  const stats = useMemo(() => {
    const assigned = assignments.filter((a) => a.structure_id).length;
    return {
      roles: salaryRoles.length,
      employees: assignments.length,
      assigned,
      unassigned: Math.max(0, assignments.length - assigned)
    };
  }, [salaryRoles, assignments]);

  const openNewRole = () => {
    setRoleForm(emptyRoleForm);
    setRoleDialogOpen(true);
  };

  const openEditRole = (role) => {
    const extras = lineItemsToSingleFields(role.allowances, role.deductions);
    setRoleForm({
      id: role.id,
      name: role.name || '',
      basic: role.basic != null ? String(role.basic) : '',
      hra: role.hra != null ? String(role.hra) : '',
      allowance: extras.allowance ? String(extras.allowance) : '',
      deduction: extras.deduction ? String(extras.deduction) : ''
    });
    setRoleDialogOpen(true);
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) {
      onFeedback('Role name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: roleForm.name.trim(),
        basic: Number(roleForm.basic) || 0,
        hra: Number(roleForm.hra) || 0,
        allowance: roleForm.allowance,
        deduction: roleForm.deduction
      };
      if (roleForm.id) {
        await apiRequest(`/api/company/hrms/payroll/salary-roles/${roleForm.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        onFeedback('Salary role updated.');
      } else {
        await apiRequest('/api/company/hrms/payroll/salary-roles', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        onFeedback('Salary role created.');
      }
      setRoleDialogOpen(false);
      setRoleForm(emptyRoleForm);
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async (roleId) => {
    if (!window.confirm('Remove this salary role?')) return;
    try {
      const data = await apiRequest(`/api/company/hrms/payroll/salary-roles/${roleId}`, { method: 'DELETE' });
      onFeedback(data.message || 'Role removed.');
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    }
  };

  const applyRoleToAssignForm = (roleId) => {
    if (!roleId) {
      setAssignForm((p) => ({ ...p, salary_role_id: '' }));
      return;
    }
    const role = salaryRoles.find((r) => String(r.id) === String(roleId));
    if (!role) return;
    const extras = lineItemsToSingleFields(role.allowances, role.deductions);
    setAssignForm((p) => ({
      ...p,
      salary_role_id: String(roleId),
      basic: role.basic != null ? String(role.basic) : '',
      hra: role.hra != null ? String(role.hra) : '',
      allowance: extras.allowance ? String(extras.allowance) : '',
      deduction: extras.deduction ? String(extras.deduction) : ''
    }));
  };

  const openAssignDialog = (row = null) => {
    setAssignLockEmployee(!!row?.candidate_id);
    if (row?.candidate_id) {
      const extras = lineItemsToSingleFields(row.allowances, row.deductions);
      setAssignForm({
        candidate_id: row.candidate_id,
        salary_role_id: row.salary_role_id ? String(row.salary_role_id) : '',
        basic: row.basic != null ? String(row.basic) : '',
        hra: row.hra != null ? String(row.hra) : '',
        allowance: extras.allowance ? String(extras.allowance) : '',
        deduction: extras.deduction ? String(extras.deduction) : ''
      });
    } else {
      setAssignForm(emptyAssignForm);
    }
    setAssignDialogOpen(true);
  };

  const saveAssignment = async () => {
    if (!assignForm.candidate_id) {
      onFeedback('Select an employee.');
      return;
    }
    setSaving(true);
    try {
      const allowances = assignForm.allowance
        ? [{ label: 'Allowance', amount: Number(assignForm.allowance) || 0 }]
        : [];
      const deductions = assignForm.deduction
        ? [{ label: 'Deduction', amount: Number(assignForm.deduction) || 0 }]
        : [];
      await apiRequest('/api/company/hrms/payroll/salary-structures', {
        method: 'POST',
        body: JSON.stringify({
          candidate_id: Number(assignForm.candidate_id),
          salary_role_id: assignForm.salary_role_id ? Number(assignForm.salary_role_id) : null,
          basic: Number(assignForm.basic) || 0,
          hra: Number(assignForm.hra) || 0,
          allowances,
          deductions
        })
      });
      onFeedback('Salary structure saved.');
      setAssignDialogOpen(false);
      setAssignForm(emptyAssignForm);
      await loadAll();
    } catch (e) {
      onFeedback(e.message);
    } finally {
      setSaving(false);
    }
  };

  const thSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#64748b',
    bgcolor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    py: 1.25
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="#0f172a">
            Salary structure
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Role templates for payroll · assign each active employee before running payroll
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${stats.roles} roles`} sx={{ bgcolor: '#ecfdf5', color: TEAL_DARK, fontWeight: 600 }} />
          <Chip size="small" label={`${stats.assigned}/${stats.employees} assigned`} color={stats.unassigned ? 'warning' : 'success'} variant="outlined" />
        </Stack>
      </Stack>

      {stats.unassigned > 0 && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {stats.unassigned} active employee(s) have no salary structure. Assign a role or custom amounts before payroll.
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
        <Tabs
          value={section}
          onChange={(_, v) => setSection(v)}
          sx={{
            px: 2,
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#fafafa',
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 48 },
            '& .Mui-selected': { color: TEAL }
          }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<RoleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Salary roles" />
          <Tab label="Employee assignments" />
        </Tabs>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {section === 0 && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search roles…"
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ maxWidth: 320, bgcolor: '#fff' }}
                />
                <Button variant="contained" startIcon={<AddIcon />} onClick={openNewRole} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
                  Add role
                </Button>
              </Stack>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Role</TableCell>
                      <TableCell sx={thSx} align="right">Basic</TableCell>
                      <TableCell sx={thSx} align="right">HRA</TableCell>
                      <TableCell sx={thSx} align="right">Allowance</TableCell>
                      <TableCell sx={thSx} align="right">Deduction</TableCell>
                      <TableCell sx={thSx} align="right">Monthly gross</TableCell>
                      <TableCell sx={thSx} align="right" width={100}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">Loading…</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && filteredRoles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <Typography color="text.secondary" gutterBottom>
                            No salary roles yet
                          </Typography>
                          <Button size="small" variant="outlined" onClick={openNewRole} sx={{ mt: 1 }}>
                            Create your first role
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading &&
                      filteredRoles.map((role) => {
                        const extras = lineItemsToSingleFields(role.allowances, role.deductions);
                        const gross = monthlyGross(role.basic, role.hra, extras.allowance);
                        return (
                          <TableRow key={role.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell>
                              <Typography fontWeight={600}>{role.name}</Typography>
                            </TableCell>
                            <TableCell align="right">{formatInr(role.basic)}</TableCell>
                            <TableCell align="right">{formatInr(role.hra)}</TableCell>
                            <TableCell align="right">{formatInr(extras.allowance)}</TableCell>
                            <TableCell align="right">{formatInr(extras.deduction)}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={700} color={TEAL_DARK}>
                                {formatInr(gross)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEditRole(role)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton size="small" color="error" onClick={() => removeRole(role.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}

          {section === 1 && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search employees…"
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ maxWidth: 320, bgcolor: '#fff' }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openAssignDialog()}
                  disabled={!activeEmployeesList.length}
                  sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}
                >
                  Assign employee
                </Button>
              </Stack>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Employee</TableCell>
                      <TableCell sx={thSx}>Role</TableCell>
                      <TableCell sx={thSx} align="right">Basic</TableCell>
                      <TableCell sx={thSx} align="right">HRA</TableCell>
                      <TableCell sx={thSx} align="right">Gross</TableCell>
                      <TableCell sx={thSx}>Status</TableCell>
                      <TableCell sx={thSx} align="right" width={80}>Edit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">Loading…</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && filteredAssignments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <Typography color="text.secondary">No active employees found.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading &&
                      filteredAssignments.map((row) => {
                        const extras = lineItemsToSingleFields(row.allowances, row.deductions);
                        const gross = monthlyGross(row.basic, row.hra, extras.allowance);
                        const hasStructure = !!row.structure_id;
                        return (
                          <TableRow key={row.candidate_id} hover sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell>
                              <Typography fontWeight={600}>{row.employee_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {[row.employee_code, row.mobile].filter(Boolean).join(' · ') || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {row.salary_role_name ? (
                                <Chip size="small" label={row.salary_role_name} variant="outlined" sx={{ fontWeight: 600 }} />
                              ) : hasStructure ? (
                                <Chip size="small" label="Custom" variant="outlined" />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{hasStructure ? formatInr(row.basic) : '—'}</TableCell>
                            <TableCell align="right">{hasStructure ? formatInr(row.hra) : '—'}</TableCell>
                            <TableCell align="right">
                              {hasStructure ? (
                                <Typography fontWeight={600}>{formatInr(gross)}</Typography>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={hasStructure ? 'Assigned' : 'Not set'}
                                color={hasStructure ? 'success' : 'default'}
                                variant={hasStructure ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={hasStructure ? 'Edit assignment' : 'Assign'}>
                                <IconButton size="small" onClick={() => openAssignDialog(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{roleForm.id ? 'Edit salary role' : 'New salary role'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Role name"
              placeholder="e.g. Field Executive, Supervisor"
              value={roleForm.name}
              onChange={(e) => setRoleForm((p) => ({ ...p, name: e.target.value }))}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label="Basic (₹)" type="number" value={roleForm.basic} onChange={(e) => setRoleForm((p) => ({ ...p, basic: e.target.value }))} />
              <TextField fullWidth size="small" label="HRA (₹)" type="number" value={roleForm.hra} onChange={(e) => setRoleForm((p) => ({ ...p, hra: e.target.value }))} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label="Allowance (₹)" type="number" value={roleForm.allowance} onChange={(e) => setRoleForm((p) => ({ ...p, allowance: e.target.value }))} />
              <TextField fullWidth size="small" label="Deduction (₹)" type="number" value={roleForm.deduction} onChange={(e) => setRoleForm((p) => ({ ...p, deduction: e.target.value }))} />
            </Stack>
            <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
              Monthly gross (for reference):{' '}
              <strong>{formatInr(monthlyGross(roleForm.basic, roleForm.hra, roleForm.allowance))}</strong>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRole} disabled={saving} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
            {roleForm.id ? 'Save changes' : 'Create role'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Assign salary to employee</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <StaffingEmployeeSelect
              employees={activeEmployeesList}
              value={assignForm.candidate_id}
              onChange={(id) => setAssignForm((p) => ({ ...p, candidate_id: id }))}
              disabled={assignLockEmployee}
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Salary role"
              value={assignForm.salary_role_id}
              onChange={(e) => applyRoleToAssignForm(e.target.value)}
              helperText={salaryRoles.length ? 'Pick a role to copy amounts, or leave empty for custom' : 'Create roles in the first tab'}
            >
              <MenuItem value="">Custom amounts</MenuItem>
              {salaryRoles.map((r) => (
                <MenuItem key={r.id} value={String(r.id)}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label="Basic (₹)" type="number" value={assignForm.basic} onChange={(e) => setAssignForm((p) => ({ ...p, basic: e.target.value }))} />
              <TextField fullWidth size="small" label="HRA (₹)" type="number" value={assignForm.hra} onChange={(e) => setAssignForm((p) => ({ ...p, hra: e.target.value }))} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth size="small" label="Allowance (₹)" type="number" value={assignForm.allowance} onChange={(e) => setAssignForm((p) => ({ ...p, allowance: e.target.value }))} />
              <TextField fullWidth size="small" label="Deduction (₹)" type="number" value={assignForm.deduction} onChange={(e) => setAssignForm((p) => ({ ...p, deduction: e.target.value }))} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAssignment} disabled={saving || !assignForm.candidate_id} sx={{ bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK } }}>
            Save assignment
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
